import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import axios from "axios";
import {Status} from "./status";
import {headers} from "./headers";
import {Contributor, Invitation, User} from "./User";
import {host} from "./api";
import {Cookies} from "react-cookie";

export interface ContributorState {
    currentUser: User | undefined,
    contributors: Array<User>,
    creator: User | undefined,
    status: Status,
    error: string | null | undefined
}

const initialState: ContributorState = {
    currentUser: undefined,
    creator: undefined,
    contributors: [],
    status: Status.Idle,
    error: null,
}


export const signin = createAsyncThunk('signin', async (user: { email: string, hash: string }) => {
    const response = await axios.post(`${host}/users/signin`, user, {
        headers: headers(undefined)
    })
    return response.data
})


export const logout = createAsyncThunk('logout', async () => {
    return 0
})

export const signup = createAsyncThunk('signup', async (user: { email: string, hash: string, code: string }) => {
    const response = await axios.post(`${host}/users/signup`, user, {
        headers: headers(undefined)
    })
    return response.data
})

export const saveUser = createAsyncThunk('saveUser', async (user: {
    avatar?: string,
    name?: string,
    hash?: string
}) => {
    const response = await axios.put(`${host}/users`, user, {
        headers: headers(undefined)
    })
    return response.data
})

export const fetchUser = createAsyncThunk('fetchUser', async () => {
    const response = await axios.get(`${host}/users`, {
        headers: headers(undefined)
    })
    return response.data
})

export const fetchContributors = createAsyncThunk('contributors', async (projectId: number | undefined) => {
    const response = await axios.get(`${host}/projects/${projectId}/contributors`, {
        headers: headers(projectId?.toString())
    })
    return response.data
})

export const updateContributor = createAsyncThunk('updateContributor', async (contributor: Contributor) => {
    const response = await axios.post(`${host}/projects/${contributor.projectId}/contributors/${contributor.userId}`,
        contributor,
        {
            headers: headers(contributor.projectId?.toString())
        })
    return response.data
})

export const deleteContributor = createAsyncThunk('deleteContributor', async (contributor: Contributor) => {
    const response = await axios.delete(`${host}/projects/${contributor.projectId}/contributors/${contributor.userId}`,
        {
            headers: headers(contributor.projectId?.toString())
        })
    return response.data
})


export const inviteContributor = createAsyncThunk('inviteContributor', async (invitation: Invitation) => {
    const response = await axios.post(`${host}/projects/${invitation.projectId}/contributors`,
        invitation,
        {
            headers: headers(invitation.projectId?.toString())
        })
    return response.data
})

export const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchContributors.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(fetchContributors.fulfilled, (state, action) => {
                state.status = Status.Succeeded
                state.contributors = action.payload
                state.creator = action.payload.find((c: User) => c.role === 'creator')
                const imContributor = action.payload.find((c: User) => c.id == state.currentUser?.id)
                state.currentUser = state.currentUser ? {
                    ...state.currentUser,
                    role: imContributor?.role,
                    status: imContributor?.status
                } : state.currentUser
            })
            .addCase(fetchContributors.rejected, (state, action) => {
                state.status = Status.Failed
                state.error = action.error.message
            })
            .addCase(updateContributor.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteContributor.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(inviteContributor.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveUser.fulfilled, (state, action) => {
                const imContributor = state.contributors.find((c: User) => c.id == action.payload.id)
                state.currentUser = {
                    ...action.payload,
                    role: imContributor?.role,
                    status: imContributor?.status
                }
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                const imContributor = state.contributors.find((c: User) => c.id == action.payload.id)
                state.currentUser = {
                    ...action.payload,
                    role: imContributor?.role,
                    status: imContributor?.status
                }
            })
            .addCase(logout.fulfilled, (state, action) => {
                state.currentUser = undefined
                new Cookies().remove('token')
            })
            .addCase(signin.fulfilled, (state, action) => {
                const imContributor = state.contributors.find((c: User) => c.id == action.payload.id)
                state.currentUser = {
                    ...action.payload,
                    role: imContributor?.role,
                    status: imContributor?.status
                }
                if (action.payload && action.payload.token) {
                    new Cookies().set('token', action.payload.token)
                }
            })
            .addCase(signin.rejected, (state, action) => {
                state.error = action.error.message
            })
            .addCase(signup.fulfilled, (state, action) => {
                const imContributor = state.contributors.find((c: User) => c.id == action.payload.id)
                state.currentUser = {
                    ...action.payload,
                    role: imContributor?.role,
                    status: imContributor?.status
                }
                new Cookies().set('token', action.payload.token)
            })
            .addCase(signup.rejected, (state, action) => {
                state.error = action.error.message
            })
    }
})

export default usersSlice.reducer;